/**
 * Sign Up Page — siu-dou design language
 *
 * AUTH CONFIGURATION - See CLAUDE.md "Auth Method Control" section
 * Email/password signup is DISABLED by default (OAuth-only mode).
 * To enable: ENABLE_EMAIL_LOGIN=true AND ENABLE_EMAIL_SIGNUP=true
 */
import { useState, useEffect, useMemo, FormEvent } from 'react'
import { authClient } from '@/client/lib/auth'
import { checkPasswordStrength } from '@/shared/lib/password-strength'
import { Wordmark, Mono, Rule, inputCss, solidBtn } from '@/client/modules/journal/components/ui'
import '@/client/modules/journal/journal.css'

interface AuthConfig {
  emailLoginEnabled: boolean
  emailSignupEnabled: boolean
  googleEnabled: boolean
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  )
}

export function SignUpPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(true)

  const passwordStrength = useMemo(() => checkPasswordStrength(password), [password])

  useEffect(() => {
    fetch('/api/auth/config')
      .then(res => { if (!res.ok) throw new Error(); return res.json() })
      .then(data => { setAuthConfig(data as AuthConfig); setConfigLoading(false) })
      .catch(() => { setAuthConfig({ emailLoginEnabled: true, emailSignupEnabled: true, googleEnabled: true }); setConfigLoading(false) })
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) { setError('Passwords do not match'); return }
    if (!passwordStrength.isValid) { setError('Please choose a stronger password'); return }
    setLoading(true)
    try {
      await authClient.signUp.email({ email, password, name })
      window.location.href = '/journal/dashboard'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setLoading(true)
    try {
      await authClient.signIn.social({ provider: 'google', callbackURL: '/journal/dashboard' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up with Google')
      setLoading(false)
    }
  }

  // Registration disabled
  if (!configLoading && !authConfig?.emailSignupEnabled && !authConfig?.googleEnabled) {
    return (
      <div className="db-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <a href="/" style={{ textDecoration: 'none' }}><Wordmark size={28} /></a>
          <p style={{ fontFamily: 'var(--db-serif-body)', fontStyle: 'italic', fontSize: 17, color: 'var(--db-ink-2)', margin: '32px auto', lineHeight: 1.55 }}>
            New accounts are currently by invitation only.
          </p>
          <a href="/sign-in" style={{ fontFamily: 'var(--db-mono)', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--db-accent-ink)', textDecoration: 'none' }}>
            ← Sign in
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <a href="/" style={{ textDecoration: 'none' }}><Wordmark size={32} /></a>
        </div>

        {configLoading ? (
          <div style={{ textAlign: 'center' }}><Mono style={{ color: 'var(--db-ink-3)' }}>Loading…</Mono></div>
        ) : (
          <>
            <h1 style={{ fontFamily: 'var(--db-serif-display)', fontWeight: 500, fontSize: 26, color: 'var(--db-ink)', margin: '0 0 8px', letterSpacing: '-0.01em', textAlign: 'center' }}>
              Begin the path
            </h1>
            <p style={{ fontFamily: 'var(--db-serif-body)', fontStyle: 'italic', fontSize: 16, color: 'var(--db-ink-2)', textAlign: 'center', margin: '0 0 36px', lineHeight: 1.5 }}>
              Create your account to start keeping the journal.
            </p>

            {error && (
              <div style={{ background: 'rgba(180,40,30,0.07)', border: '1px solid rgba(180,40,30,0.18)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                <p style={{ fontFamily: 'var(--db-serif-body)', fontSize: 15, color: '#9b2c1a', margin: 0 }}>{error}</p>
              </div>
            )}

            {authConfig?.emailSignupEnabled && (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { label: 'Your name', type: 'text', value: name, set: setName, placeholder: 'Sam Chan', autoFocus: true },
                  { label: 'Email', type: 'email', value: email, set: setEmail, placeholder: 'you@example.com' },
                  { label: 'Password', type: 'password', value: password, set: setPassword, placeholder: '' },
                  { label: 'Confirm password', type: 'password', value: confirmPassword, set: setConfirmPassword, placeholder: '' },
                ].map(f => (
                  <div key={f.label} style={{ marginBottom: 16 }}>
                    <Mono style={{ fontSize: 10, color: 'var(--db-ink-3)', marginBottom: 8, display: 'block' }}>{f.label}</Mono>
                    <input
                      type={f.type}
                      value={f.value}
                      onChange={e => f.set(e.target.value)}
                      placeholder={f.placeholder}
                      required
                      disabled={loading}
                      autoFocus={f.autoFocus}
                      style={inputCss}
                    />
                  </div>
                ))}

                {/* Password strength indicator */}
                {password.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginBottom: 16, marginTop: -8 }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 2,
                        background: i <= passwordStrength.score
                          ? passwordStrength.score >= 3 ? 'var(--db-accent)' : 'rgba(180,40,30,0.4)'
                          : 'var(--db-line)',
                        transition: 'background 0.3s',
                      }} />
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...solidBtn, width: '100%', padding: '14px', marginTop: 12, borderRadius: 999 }}
                >
                  {loading ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            )}

            {authConfig?.googleEnabled && (
              <>
                {authConfig?.emailSignupEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                    <Rule style={{ flex: 1 }} />
                    <Mono style={{ fontSize: 9.5, color: 'var(--db-ink-3)' }}>or</Mono>
                    <Rule style={{ flex: 1 }} />
                  </div>
                )}
                <button
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '13px',
                    background: 'var(--db-paper-lift)',
                    border: '1px solid var(--db-line)',
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    fontFamily: 'var(--db-mono)',
                    fontSize: 10.5,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'var(--db-ink)',
                  }}
                >
                  <GoogleIcon />
                  {loading ? 'Signing up…' : 'Continue with Google'}
                </button>
              </>
            )}

            <p style={{ fontFamily: 'var(--db-serif-body)', fontSize: 15, color: 'var(--db-ink-2)', textAlign: 'center', marginTop: 28 }}>
              Already have an account?{' '}
              <a href="/sign-in" style={{ color: 'var(--db-accent-ink)', borderBottom: '1px solid color-mix(in srgb, var(--db-accent) 40%, transparent)', textDecoration: 'none' }}>
                Sign in
              </a>
            </p>
          </>
        )}

        <div style={{ marginTop: 56, textAlign: 'center' }}>
          <Rule style={{ width: 32, margin: '0 auto 16px' }} />
          <span style={{ fontFamily: 'var(--db-han)', fontSize: 18, color: 'var(--db-ink-3)' }}>Daybook</span>
        </div>
      </div>
    </div>
  )
}
